import { useEffect, useCallback, useRef } from 'react';
import { tokenManager } from '../services/api';
import authService from '../services/authService';

const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

const useSessionTimeout = () => {
  const hasTriggered = useRef(false);

  const isTokenExpired = useCallback(() => {
    const expiresAt = tokenManager.getSessionExpiry();
    if (!expiresAt) return false;
    return new Date() >= new Date(expiresAt);
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (hasTriggered.current) return;
    hasTriggered.current = true;
    authService.logout();
    window.location.href = '/login?sessionExpired=true';
  }, []);

  const validateWithBackend = useCallback(async () => {
    try {
      const result = await authService.validateToken();
      if (!result.success) {
        handleSessionExpired();
      }
    } catch {
      // If validation fails due to network error, fall back to local check
      if (isTokenExpired()) {
        handleSessionExpired();
      }
    }
  }, [isTokenExpired, handleSessionExpired]);

  // Periodic session check
  useEffect(() => {
    // KAN-149: session presence is the cookie-backed `user`, not a localStorage token.
    if (!authService.isAuthenticated()) return;

    const checkSession = () => {
      if (isTokenExpired()) {
        handleSessionExpired();
      } else {
        validateWithBackend();
      }
    };

    // Check immediately on mount
    checkSession();

    // Then check every 5 minutes
    const intervalId = setInterval(checkSession, CHECK_INTERVAL);

    // Also check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTokenExpired, handleSessionExpired, validateWithBackend]);

  // Cross-tab logout detection
  useEffect(() => {
    const handleStorage = (e) => {
      // KAN-149: cross-tab logout is detected via the `auth_user` key (the token is no longer
      // stored); tokenManager.clearAll() removes it on logout.
      if (e.key === 'auth_user' && !e.newValue) {
        if (!hasTriggered.current) {
          hasTriggered.current = true;
          window.location.href = '/login';
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
};

export default useSessionTimeout;
